import { MatDialog } from '@angular/material/dialog';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';
import { UserScopeService, UserScope, restrictToScope, inScope } from '@app/shared/services/user-scope.service';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
import { LoggerService } from '@app/logger/logger.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { SimplePdfService } from '@app/shared/services/simple-pdf.service';

import { InterventionsService } from '@app/shared/services/entities/interventions.service';
import { PatientsService } from '@app/shared/services/entities/patients.service';
import { DoctorsService } from '@app/shared/services/entities/doctors.service';
import { ProcedureService } from '@app/shared/services/entities/procedure.service';
import { SpecialtyService } from '@app/shared/services/entities/specialty.service';
import { OperationRoomsService } from '@app/shared/services/entities/operationRooms.service';
import { PlacesService } from '@app/shared/services/entities/places.service';
import { ContractsService } from '@app/shared/services/entities/contracts.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { WarehousesService } from '@app/shared/services/entities/warehouse.service';
import { StockService } from '@app/shared/services/entities/stock.service';
import { ServicePackagesService } from '@app/shared/services/entities/servicePackages.service';
import { HospitalServicesService } from '@app/shared/services/entities/hospitalServices.service';
import { EquipmentsService } from '@app/shared/services/entities/equipments.service';
import { KitService } from '@app/shared/services/entities/kit.service';
import { UsersService } from '@app/shared/services/entities/users.service';
import { RolesService } from '@app/shared/services/entities/roles.service';
import { JetstreamService } from '@app/shared/services/jetstream.service';
import { TimeWheelComponent } from '@app/shared/components/time-wheel/time-wheel.component';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { ManufacturerService } from '@app/shared/services/entities/manufacturers.service';
import { FamilyService } from '@app/shared/services/entities/family.service';
import { GaPdfService } from '@app/shared/pdf/ga-pdf.service';
import { buildFolioDoc } from '@app/shared/pdf/ga-forms';
import { buildPreferenceDoc, GaPreferenceData, GaPreferenceSurgery } from '@app/shared/pdf/ga-bitacoras';
import { GaFormsService } from '@app/shared/services/entities/ga-forms.service';
import { canEditFolio, canSignFolio } from '@app/shared/utils/folio-edit';

const log = new LoggerService('INTERVENTIONS WIZARD');

/**
 * Asistente de folio de salida de material (flujo Medallia compacto).
 *
 *   1. Técnico y médico   →  técnico Medtronic (radial) y médico (alta rápida)
 *   2. Folio y servicio   →  hospital, fecha con calendario + hora 00:00-23:59,
 *                            folio por serie del hospital, servicio
 *   3. Cirugía y equipo   →  CIR, paciente, horas de cx, charola, lentes,
 *                            SONICISION, torre, energía de otra casa
 *   4. Material           →  RFID (widget/bandeja/anaquel/pistola) + Otros;
 *                            cada pieza se aparta (Transito) al agregarla
 *   5. Paquete            →  sugiere el paquete del contrato con mayor cobertura
 *   6. Vale               →  PDF con folio, QR y firmas; el folio pasa de
 *                            Incompleto a Pendiente
 *
 * La remisión ya NO es un paso: se captura aparte en /interventions/remission.
 * El borrador (status Incompleto) se retoma desde la lista de Salida de material.
 */
import { isValidRfidTag, RFID_TAG_LENGTH } from '@app/shared/utils/rfid';
/** "JUAN PÉREZ de la O" → "Juan Pérez De La O": 1a letra de cada palabra en mayúscula. */
export function titleCaseName(value: any): string {
  return String(value ?? '').toLocaleLowerCase('es')
    .replace(/(^|[\s\-'.])(\p{L})/gu, (_m, sep, ch) => sep + ch.toLocaleUpperCase('es'))
    .replace(/\s+/g, ' ').trim();
}

/** Para comparar nombres: sin acentos, mayúsculas, sin signos, espacios simples. */
/** Parecido entre dos textos (0-1) con distancia de Levenshtein. */
function similarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const m = a.length, n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return 1 - prev[n] / Math.max(m, n);
}

function normName(value: any): string {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase().replace(/[^A-Z0-9Ñ ]/g, ' ').replace(/\s+/g, ' ').trim();
}

@Component({
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MaterialModule, TimeWheelComponent, AutoSelectComponent],
  selector: 'app-interventions-wizard',
  templateUrl: './interventions-wizard.component.html',
  styleUrls: ['./interventions-wizard.component.scss'],
})
export class InterventionsWizardComponent implements OnInit {
  public step = 0; // índice 0-5
  // Flujo Medallia compacto: las 13 pantallas del forms se agrupan en los
  // primeros 4 pasos; paquete/remisión/vale completan el proceso del sistema.
  public readonly stepTitles = ['Técnico, médico y folio', 'Cirugía y equipo', 'Material', 'CIR', 'PDF', 'Firma'];
  private readonly dialog = inject(MatDialog);
  /** Vale firmado (key de S3) elegido en el paso de Firma. */
  public signedKey = '';

  public record: any = null; // la intervención (existe desde que se completa el paso 1)

  // ── Paso 1 ──
  public generalForm!: FormGroup;
  public specialtyList: any[] = [];
  public procedureList: any[] = [];
  public doctorList: any[] = [];
  public operationRoomList: any[] = [];
  public placesList: any[] = [];
  public patientList: any[] = [];
  public contractList: any[] = [];
  public patientFilter = '';
  public creatingPatient = false;
  public newPatient: any = { name: null, CURP: null, birthDate: null, gender: null };
  public creatingDoctor = false;
  public newDoctor: any = { name: null, specialty: null, place: null };
  public creatingProcedure = false;
  public newProcedure: any = { name: null, specialty: null };
  public hospitalServicesList: any[] = [];
  /** Catálogo de Equipos: la Torre se elige de los equipos del hospital. */
  public equipmentsList: any[] = [];
  /** Catálogo de Fabricantes (energía de otra casa comercial). */
  public manufacturersList: any[] = [];
  /** Paso CIR: quién / por qué se agregó material fuera del CIR (obligatorio si hay). */
  public outOfCirReason = '';
  private manufacturerService = inject(ManufacturerService);
  private familyService = inject(FamilyService);
  private gaPdf = inject(GaPdfService);
  private gaForms = inject(GaFormsService);

  // ── Preferencia del médico (vista previa por Cx al elegir hospital y médico) ──
  public preference: GaPreferenceData | null = null;
  public preferenceLoading = false;
  public preferenceSurgery = 0;
  private preferenceKey = '';

  public get preferenceSelected(): GaPreferenceSurgery | null {
    return this.preference?.surgeries?.[this.preferenceSurgery] ?? null;
  }

  async refreshPreference(): Promise<void> {
    const doctor = this.generalForm?.get('doctor')?.value;
    const place = this.generalForm?.get('hospital')?.value;
    const key = doctor && place ? `${doctor}|${place}` : '';
    if (key === this.preferenceKey) return;
    this.preferenceKey = key;
    this.preference = null;
    this.preferenceSurgery = 0;
    if (!key) return;
    this.preferenceLoading = true;
    try {
      const data = await this.gaForms.doctorPreference(doctor, place);
      if (this.preferenceKey !== key) return;          // cambió mientras cargaba
      this.preference = data;
      // Si ya se eligió la Cx, se abre la de esa Cx.
      const procName = normName(this.procedureList.find(p => p?.id === this.generalForm.get('procedure')?.value)?.name);
      const idx = procName ? data.surgeries.findIndex(sg => normName(sg.surgery) === procName) : -1;
      this.preferenceSurgery = idx >= 0 ? idx : 0;
    } catch {
      if (this.preferenceKey === key) this.preference = null;
    } finally {
      if (this.preferenceKey === key) this.preferenceLoading = false;
    }
  }

  /** Descarga el formato de preferencia SOLO de la Cx que se está viendo. */
  downloadPreference(): void {
    const d = this.preference, sg = this.preferenceSelected;
    if (!d || !sg) return;
    const doc = buildPreferenceDoc({ ...d, surgeries: [sg], omittedSurgeries: 0 });
    const safe = (t: string) => normName(t).replace(/\s+/g, '-') || 'formato';
    this.gaPdf.download(doc, `Preferencia-${safe(d.doctor)}-${safe(sg.surgery)}`);
  }
  /** Familias (columna COT del formato: EMID, SES, STI…). */
  public familiesList: any[] = [];
  private readonly equipmentsService = inject(EquipmentsService);
  public kitsList: any[] = [];

  // ── Equipo de cirugía (campos del reporte de campo / forms Medallia) ──
  public equipoOpen = false;
  public equipo: any = {
    tray: null, trayOther: null, trayDetail: null,
    cabezal: null, lente1: null, lente2: null, lente3: null,
    torre: null,
    sonicisionUsed: false, generator1: null, generator2: null, battery1: null, battery2: null,
    otherEnergyUsed: false, otherEnergyDetail: null, // marca/producto de la otra casa comercial
  };

  /** SONICISION y energía de otra casa comercial son excluyentes (uno u otro).
   *  Al prender uno se apaga y limpia el otro. */
  onSonicisionChange(used: boolean): void {
    if (used) { this.equipo.otherEnergyUsed = false; this.equipo.otherEnergyDetail = null; }
  }

  onOtherEnergyChange(used: boolean): void {
    if (used) {
      this.equipo.sonicisionUsed = false;
      this.equipo.generator1 = null; this.equipo.generator2 = null;
      this.equipo.battery1 = null; this.equipo.battery2 = null;
    }
  }

  // ── Fecha con calendario + horas con el selector nativo de rueda
  //    (dos columnas: 00-23 : 00-59) ──
  public interventionDate: Date | null = null;
  public interventionTimeStr: string | null = null; // 'HH:mm'

  // ── Técnico Medtronic (pantalla 2 del forms) ──
  public technicianList: any[] = [];       // usuarios con rol Técnico
  public technicianLocked = false;         // el usuario actual ES técnico: queda fijo
  public technicianLockedName = '';

  // ── Folio por hospital (pantalla 4 del forms) ──
  public folioPreview: string | null = null;

  // ── Material: lectura RFID (widget / bandeja / anaquel / pistola) ──
  public rfidMode: 'widget' | 'bandeja' | 'anaquel' | 'pistola' = 'widget';
  public deviceName = '';
  public reading = false;
  public readTags: { epc: string; stockItem: any | null; selected: boolean; already: boolean }[] = [];

  // ── Otros (insumos fuera de inventario: CFN / piezas / lote) ──
  public otherItems: { cfn: string | null; pieces: number | null; batch: string | null }[] = [];
  public otherAdd: any = { cfn: null, pieces: 1, batch: null };

  // ── Paso Material ──
  public warehousesList: any[] = [];
  public warehouseOptions: any[] = [];
  public selectedWarehouse: string | null = null;
  public scanValue = '';
  public pieces: any[] = [];
  public productsList: any[] = [];
  public productsObject: { [id: string]: any } = {};
  public rowAdd: any = { product: null, batch: null, quantity: 1 };
  public rowProductFilter = '';

  // ── Paso 3 ──
  public packagesList: any[] = [];
  public suggestions: any[] = [];
  public selectedPackageId: string | null = null;
  public coveredPieces: any[] = [];
  public outPieces: any[] = [];

  // ── Paso Vale ──
  public valeGenerated = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private customMessageService: CustomMessageService,
    private simplePdf: SimplePdfService,
    private interventionsService: InterventionsService,
    private patientsService: PatientsService,
    private doctorsService: DoctorsService,
    private procedureService: ProcedureService,
    private specialtyService: SpecialtyService,
    private operationRoomsService: OperationRoomsService,
    private placesService: PlacesService,
    private contractsService: ContractsService,
    private productsService: ProductsService,
    private warehousesService: WarehousesService,
    private stockService: StockService,
    private servicePackagesService: ServicePackagesService,
    private hospitalServicesService: HospitalServicesService,
    private kitService: KitService,
    private usersService: UsersService,
    private rolesService: RolesService,
    private jetstreamService: JetstreamService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.generalForm = this.formBuilder.group({
      // Folio: lo escribe el técnico (campo abierto, ya no se calcula).
      fileNumber: [null, Validators.required],
      interventionDatetime: [null, Validators.required],
      type: [null],
      specialty: [null, Validators.required],
      procedure: [null, Validators.required],
      doctor: [null, Validators.required],
      episode: [null],
      operationRoom: [null, Validators.required],
      technician: [null, Validators.required],
      hospital: [null, Validators.required],
      patient: [null, Validators.required],
      // Reporte de campo (forms Medallia)
      service: [null],
      cir: [null],
      startHour: [null],
      finishHour: [null],
      comments: [null],
    });
    // Vista previa de la preferencia del médico (hospital + médico).
    this.generalForm.get('doctor')?.valueChanges.subscribe(() => void this.refreshPreference());

    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Cargando catálogos...', loading: true });
    try {
      await Promise.all([
        this.load(this.specialtyService, l => this.specialtyList = l),
        this.load(this.procedureService, l => this.procedureList = l),
        this.load(this.doctorsService, l => this.doctorList = l),
        this.load(this.operationRoomsService, l => this.operationRoomList = l),
        this.load(this.placesService, l => this.placesList = l),
        this.load(this.patientsService, l => this.patientList = l),
        this.load(this.warehousesService, l => this.warehousesList = l),
        this.load(this.hospitalServicesService, l => this.hospitalServicesList = l),
        this.load(this.equipmentsService, l => this.equipmentsList = l),
        this.load(this.manufacturerService, l => this.manufacturersList = l),
        this.load(this.familyService, l => this.familiesList = l),
        this.load(this.kitService, l => this.kitsList = l),
        this.loadProducts(),
        this.loadContracts(),
        this.loadTechnicians(),
        this.userScopeService.load().then(scope => this.scope = scope),
      ]);
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        await this.resumeDraft(id);
        // Técnico: solo sus folios y dentro de su turno (el API también lo valida).
        if (this.record && !(await this.userCanEdit(this.record)) && (await this.userCanSign(this.record))) {
          // Fuera de su turno el técnico ya no edita, pero sí sube el vale firmado.
          this.signOnly = true;
          this.step = 5;
        } else if (this.record && !(await this.userCanEdit(this.record))) {
          this.customMessageService.close();
          await this.customMessageService.open({
            title: 'Sin permiso', type: 'warning', dismissBtn: true,
            message: 'Este folio ya no se puede modificar: solo el técnico que lo capturó puede hacerlo y únicamente hasta el fin de su turno. Un coordinador puede ajustarlo.',
          });
          this.router.navigate(['/interventions/folios']);
          return;
        }
      }
      else this.setDefaultDatetime();
      this.customMessageService.close();
    } catch (err) {
      log.error('carga inicial', err);
      this.customMessageService.close(); // cerrar el "Cargando..." antes del error
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true, dismissBtnText: 'Cerrar' });
    }
  }

  /** Solo se puede subir el vale (fuera del turno del técnico). */
  signOnly = false;

  private async userCanSign(record: any): Promise<boolean> {
    try {
      const userId = sessionStorage.getItem('userId');
      const resp: any = await this.usersService.getRolNameByUserId({ id: userId });
      return canSignFolio(record, userId, resp?.data?.[0]?.roles ?? null);
    } catch {
      return false;
    }
  }

  /** ¿El usuario en sesión puede modificar este folio? (ver shared/utils/folio-edit.ts) */
  private async userCanEdit(record: any): Promise<boolean> {
    try {
      const userId = sessionStorage.getItem('userId');
      const resp: any = await this.usersService.getRolNameByUserId({ id: userId });
      return canEditFolio(record, userId, resp?.data?.[0]?.roles ?? null);
    } catch {
      return true; // si no se pudo saber el rol, decide el API al guardar
    }
  }

  private async load(service: any, assign: (list: any[]) => void): Promise<void> {
    const response = await service.search({ searchType: 'default', condition: { deleted: false } });
    assign(((response.data as any[]) ?? []).filter(x => x !== null));
  }

  private async loadProducts(): Promise<void> {
    const response = await this.productsService.search({ searchType: 'default', condition: { deleted: false } });
    this.productsList = ((response.data as any[]) ?? []).filter(x => x !== null);
    this.productsObject = this.productsList.reduce((r, a) => { r[a.id] = a; return r; }, Object.create(null));
  }

  private async loadContracts(): Promise<void> {
    // Ojo: `status` NO va en la condición — en BD es columna de texto legacy
    // y "text = boolean" truena en Postgres. Se filtra del lado del cliente.
    const response = await this.contractsService.search({ searchType: 'default', condition: { deleted: false } });
    this.contractList = ((response.data as any[]) ?? [])
      .filter(x => x !== null && (x['status'] === true || x['status'] === 'true'));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Reanudar borrador
  // ─────────────────────────────────────────────────────────────────────────

  private async resumeDraft(id: string): Promise<void> {
    const response = await this.interventionsService.search({ searchType: 'default', condition: { deleted: false, id } });
    const record = (response.data as any[])?.[0];
    if (!record) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: 'No se encontró el borrador.', dismissBtn: true });
      this.router.navigate(['/interventions/folios']);
      return;
    }
    this.record = record;
    this.generalForm.patchValue({
      interventionDatetime: record.interventionDatetime,
      type: record.type === 'Borrador' ? null : record.type,
      specialty: record.specialty,
      procedure: record.procedure,
      doctor: record.doctor,
      episode: record.episode,
      // Quirófano es texto libre; los borradores viejos guardaban el id del catálogo.
      operationRoom: this.operationRoomList.find(x => x?.id === record.operationRoom)?.name ?? record.operationRoom ?? null,
      hospital: record.hospital,
      patient: record.patient,
      service: record.service ?? null,
      technician: record.technician ?? null,
      cir: record.cir ?? null,
      startHour: record.startHour ?? null,
      finishHour: record.finishHour ?? null,
      comments: record.comments ?? null,
      fileNumber: record.fileNumber ?? null,
    });
    if (record.surgicalEquipment) {
      this.equipo = { ...this.equipo, ...record.surgicalEquipment };
      this.equipoOpen = true;
    }
    this.decomposeDatetimes(record);
    this.otherItems = Array.isArray(record.otherItems) ? [...record.otherItems] : [];
    this.folioPreview = record.fileNumber ?? null;
    this.onHospitalChange();
    this.selectedWarehouse = record.supplier?.wizardWarehouse ?? null;

    // Reconstruir las piezas apartadas desde stock (products.delivery = ids)
    const ids: string[] = record.products?.delivery ?? [];
    if (ids.length > 0 && this.selectedWarehouse) {
      const stockResponse = await this.stockService.search({
        searchType: 'default', condition: { deleted: false, warehouse: this.selectedWarehouse },
      });
      const byId = new Map(((stockResponse.data as any[]) ?? []).map(x => [x.id, x]));
      this.pieces = ids.map(pid => this.decorate(byId.get(pid))).filter(x => x);
    }
    this.selectedPackageId = record.servicePackage
      ?? (record.packageConfiguration === InterventionsWizardComponent.SERVICE_PKG ? InterventionsWizardComponent.SERVICE_PKG : null);
    await this.loadPackagesForContract();
    if (this.selectedPackageId) this.classify();

    // wizardStep guarda la serie histórica (1 datos, 2 material, 3 paquete,
    // 5 vale); los índices de pantalla van corridos +2 por los pasos de
    // técnico y folio, y ya no existe el paso de remisión (vive aparte).
    // Folio ya completado (Pendiente de firma) o firmado: se abre en el paso de Firma.
    const signed = ((record.attachments ?? []) as any[]).find(a => a?.type === 'FIRMADO');
    this.signedKey = signed?.key ?? '';
    this.outOfCirReason = record.outOfCirReason ?? '';
    // (los pasos 1 y 2 de pantalla se juntaron: el corrimiento ahora es +1)
    this.step = (record.status === 'Pendiente' || record.status === 'Realizada' || record.status === 'Firmado' || Number(record.wizardStep ?? 0) >= 5)
      ? 5
      : Math.min(Number(record.wizardStep ?? 1) + 1, 4);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Paso 1 — Datos generales
  // ─────────────────────────────────────────────────────────────────────────

  public get filteredPatients(): any[] {
    const f = this.patientFilter.trim().toLowerCase();
    const base = f
      ? this.patientList.filter(p => String(p.name ?? '').toLowerCase().includes(f) || String(p.CURP ?? '').toLowerCase().includes(f))
      : this.patientList;
    return base.slice(0, 30);
  }

  public patientName(id: string): string {
    return this.patientList.find(p => p.id === id)?.name ?? '—';
  }

  /** Cantidades siempre enteras (sin decimales). */
  public toInt(value: any, minimum = 0): number {
    const n = Math.floor(Number(value) || 0);
    return Math.max(minimum, n);
  }

  // ── Etiquetas de los autocompletes ──
  public productLabel = (product: any) => `${product?.code ?? ''} — ${product?.description ?? ''}`;
  public patientLabel = (patient: any) => `${patient?.name ?? ''} — ${patient?.CURP || 'sin CURP'}`;

  /** Charolas: los kits del catálogo más la opción "Otra...". */
  public get trayOptions(): any[] {
    return [...this.kitsList, { id: 'OTRA', name: 'Otra...' }];
  }

  /** El hospital se muestra por su ALIAS (catálogo de Sitios); si no tiene, por su nombre. */
  public placeAliasLabel = (place: any) => String(place?.alias || place?.name || '');

  /** Hospitales/especialidades asignados al usuario (null = ve todo). */
  public scope: UserScope = { places: null, specialties: null };
  private userScopeService = inject(UserScopeService);

  /** Hospitales que puede elegir el usuario. Conserva el del borrador que se
   *  está retomando aunque ya no esté en su alcance, para no perderlo. */
  public get scopedPlaces(): any[] {
    const current = this.generalForm?.get('hospital')?.value;
    // Solo sitios tipo Hospital (no Macrocentro ni almacenes) y ACTIVOS,
    // dentro del alcance del usuario.
    return this.placesList.filter(p => p?.id === current
      || (String(p?.type ?? '').toUpperCase() === 'HOSPITAL'
          && (p?.active === true || String(p?.active).toLowerCase() === 'true')
          && inScope(this.scope.places, p?.id, false)));
  }

  /** Energía de otra casa comercial: fabricantes ACTIVOS del catálogo, sin
   *  Medtronic (es la casa propia). Se guarda el NOMBRE, como antes el texto
   *  libre, y se conserva un valor ya capturado aunque no esté en el catálogo. */
  public get otherEnergyOptions(): any[] {
    const names = new Set<string>();
    const opts = this.manufacturersList
      .filter(m => m && m.active !== false)
      .map(m => String(m.name ?? '').trim())
      .filter(n => n && !/medtronic/i.test(n.normalize('NFD').replace(/[\u0300-\u036f]/g, '')) && !names.has(n) && names.add(n))
      .sort((a, b) => a.localeCompare(b, 'es'))
      .map(n => ({ id: n, name: n }));
    const current = this.equipo?.otherEnergyDetail;
    if (current && !names.has(current)) opts.unshift({ id: current, name: current });
    return opts;
  }

  /** Torres: equipos ACTIVOS del hospital elegido en el paso 1 (se conserva
   *  la torre ya capturada aunque no esté en la lista). */
  public get towerOptions(): any[] {
    const hospital = this.generalForm?.get('hospital')?.value;
    const names = new Set<string>();
    const opts = this.equipmentsList
      .filter(e => e && e.active !== false && (!hospital || e.place === hospital))
      // Los generadores no son torre: no aparecen en la lista.
      .filter(e => !/GENERADOR|GENERATOR/.test(normName(e.name)))
      .map(e => String(e.name ?? '').trim()).filter(n => n && !names.has(n) && names.add(n))
      .sort((a, b) => a.localeCompare(b, 'es', { numeric: true }))
      .map(n => ({ id: n, name: n }));
    const current = this.equipo?.torre;
    if (current && !names.has(current)) opts.unshift({ id: current, name: current });
    return opts;
  }

  /** Paso 2: al elegir el servicio hospitalario se llenan CIR y Especialidad (paso 3). */
  public onServiceChange(): void {
    const service = this.hospitalServicesList.find(s => s?.id === this.generalForm.get('service')?.value);
    if (!service) return;
    const patch: any = {};
    if (service.cir) patch.cir = service.cir;
    if (service.specialty && service.specialty !== this.generalForm.get('specialty')?.value) {
      patch.specialty = service.specialty;   // la Cx elegida se conserva
    }
    this.generalForm.patchValue(patch);
  }

  public get scopedSpecialties(): any[] {
    const current = this.generalForm?.get('specialty')?.value;
    return this.specialtyList.filter(s => inScope(this.scope.specialties, s?.id, false) || s?.id === current);
  }

  /** Hospitales donde opera un médico (lista nueva `places`, o el campo
   *  viejo `place`). Vacío = sin hospital asignado. */
  private doctorPlaces(doctor: any): string[] {
    if (Array.isArray(doctor?.places) && doctor.places.length) return doctor.places;
    return doctor?.place ? [doctor.place] : [];
  }

  /**
   * Médicos que se ofrecen: SOLO se filtra por hospital (no por especialidad).
   *  · Con hospital elegido: los que operan en ese hospital.
   *  · Sin hospital todavía (el médico se elige en el paso 1): los que operan
   *    en los hospitales asignados al usuario; si no tiene, todos.
   * Un médico sin hospitales asignados se ofrece siempre, y el ya elegido se
   * conserva para no perderlo al retomar un borrador.
   */
  public get doctorsOfHospital(): any[] {
    const hospital = this.generalForm?.get('hospital')?.value;
    const current = this.generalForm?.get('doctor')?.value;
    const allowed: string[] | null = hospital ? [hospital] : this.scope.places;
    return this.doctorList.filter(d => {
      if (d?.id === current) return true;
      const places = this.doctorPlaces(d);
      return !allowed || places.length === 0 || places.some(p => allowed.includes(p));
    });
  }

  /** Sitios tipo Hospital: opciones del hospital del médico (alta rápida). */
  public get hospitalPlaces(): any[] {
    return this.placesList.filter(place => String(place?.type ?? '').toUpperCase() === 'HOSPITAL');
  }

  /** Cx: todas (se elige primero). Al elegirla se llena su especialidad; si el
   *  técnico cambia la especialidad después, la Cx se queda como está. */
  public get proceduresOfSpecialty(): any[] {
    return this.procedureList.filter(p => p && p.active !== false);
  }

  onProcedureChange(): void {
    const p = this.procedureList.find(x => x?.id === this.generalForm.get('procedure')?.value);
    if (p?.specialty) this.generalForm.get('specialty')?.setValue(p.specialty);
  }

  async createPatient(): Promise<void> {
    if (!String(this.newPatient.name ?? '').trim()) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'El paciente necesita al menos el nombre.', dismissBtn: true });
      return;
    }
    try {
      const response: any = await this.patientsService.create({ name: String(this.newPatient.name).trim(), active: true });
      if (response.state === 'success') {
        const created = response.data[0];
        this.patientList.push(created);
        this.generalForm.get('patient')?.setValue(created.id);
        this.creatingPatient = false;
        this.newPatient = { name: null, CURP: null, birthDate: null, gender: null };
      }
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  /** Alias del hospital del paso 1 (se muestra fijo al dar de alta un médico). */
  public get selectedHospitalLabel(): string {
    const p = this.placesList.find(x => x?.id === this.generalForm?.get('hospital')?.value);
    return p ? String(p.alias || p.name || '') : '';
  }

  /** Etiqueta de médicos en el asistente: Nombre Propio (en BD van en mayúsculas). */
  public readonly doctorLabel = (d: any): string => titleCaseName(d?.name);

  /** Servicio hospitalario: se muestra el CÓDIGO MDT (si no tiene, el nombre). */
  public readonly serviceLabel = (sv: any): string =>
    String(sv?.mdtCode ?? '').trim() || String(sv?.name ?? '').trim();

  /**
   * Mientras se escribe el nombre del médico nuevo: médicos YA existentes
   * (de cualquier hospital) cuyo nombre se parece — todas las palabras
   * escritas aparecen en su nombre o en sus variantes. Así no se duplica.
   */
  public get similarDoctors(): any[] {
    const typed = normName(this.newDoctor?.name);
    const words = typed.split(' ').filter(w => w.length >= 2);
    if (!words.length) return [];
    return this.doctorList.filter(d => {
      const names = [d?.name, ...(Array.isArray(d?.nameVariants) ? d.nameVariants : [])].map(normName);
      return names.some(n => {
        const tokens = n.split(' ');
        return words.every(w => tokens.some(t => t.startsWith(w)));
      });
    }).slice(0, 6);
  }

  /** ¿El médico ya opera en el hospital elegido? */
  public doctorInHospital(d: any): boolean {
    const place = this.generalForm?.get('hospital')?.value;
    return !!place && this.doctorPlaces(d).includes(place);
  }

  public doctorHospitalsLabel(d: any): string {
    const names = this.doctorPlaces(d)
      .map(id => this.placesList.find(p => p?.id === id))
      .map(p => p?.alias || p?.name).filter(Boolean);
    return names.length ? names.join(', ') : 'Sin hospital';
  }

  /**
   * Usar un médico existente en lugar de darlo de alta: si no opera en el
   * hospital elegido, se le AGREGA ese hospital a su lista (`places`).
   */
  async useExistingDoctor(doctor: any): Promise<void> {
    const place = this.generalForm.get('hospital')?.value || null;
    if (!place) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Elige primero el hospital.', dismissBtn: true });
      return;
    }
    try {
      if (!this.doctorInHospital(doctor)) {
        const places = Array.from(new Set([...this.doctorPlaces(doctor), place]));
        const response: any = await this.doctorsService.update({ id: doctor.id, places, operationType: 'single' } as any);
        if (response?.state !== 'success') throw new Error(response?.message ?? 'No se pudo actualizar el médico.');
        const idx = this.doctorList.findIndex(d => d?.id === doctor.id);
        const updated = { ...doctor, ...(response.data?.[0] ?? {}), places };
        if (idx >= 0) this.doctorList[idx] = updated;
        this.doctorList = [...this.doctorList];
      }
      this.generalForm.get('doctor')?.setValue(doctor.id);
      this.creatingDoctor = false;
      this.newDoctor = { name: null, specialty: null, place: null };
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  async createDoctor(): Promise<void> {
    if (!String(this.newDoctor.name ?? '').trim()) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'El médico necesita nombre.', dismissBtn: true });
      return;
    }
    // Si ya existe EXACTAMENTE con ese nombre, no se duplica: se usa el existente.
    const typed = normName(this.newDoctor.name);
    const exact = this.doctorList.find(d =>
      [d?.name, ...(Array.isArray(d?.nameVariants) ? d.nameVariants : [])].some(n => normName(n) === typed));
    if (exact) {
      const use = await this.customMessageService.open({
        title: 'El médico ya existe', type: 'warning', dismissBtn: true, acceptBtn: true,
        message: `<b>${titleCaseName(exact.name)}</b> ya está dado de alta (${this.doctorHospitalsLabel(exact)}). ` +
                 (this.doctorInHospital(exact) ? 'Se seleccionará.' : 'Se le agregará este hospital y se seleccionará.'),
      });
      if (use) await this.useExistingDoctor(exact);
      return;
    }
    // El médico nuevo se da de alta SIEMPRE en el hospital elegido en el paso 1.
    const place = this.generalForm.get('hospital')?.value || null;
    if (!place) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Elige primero el hospital: el médico nuevo se da de alta en ese hospital.', dismissBtn: true });
      return;
    }
    this.newDoctor.place = place;
    this.newDoctor.places = [place];
    try {
      // Alta rápida (diseño de operación §2): solo nombre y especialidad, y se
      // marca el origen para que quien cura el catálogo pueda revisarla luego.
      const response: any = await this.doctorsService.create({ ...this.newDoctor, name: titleCaseName(this.newDoctor.name), active: true, createdFrom: 'intervencion' });
      if (response.state === 'success') {
        const created = response.data[0];
        this.doctorList.push(created);
        this.generalForm.get('doctor')?.setValue(created.id);
        this.creatingDoctor = false;
        this.newDoctor = { name: null, specialty: null, place: null };
      }
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  /** Alta rápida de cx (procedimiento): nombre + especialidad. */
  /** Abre / cierra la captura de una Cx nueva con la especialidad ya elegida. */
  toggleNewProcedure(): void {
    this.creatingProcedure = !this.creatingProcedure;
    if (this.creatingProcedure) {
      this.newProcedure = { name: null, specialty: this.generalForm.get('specialty')?.value ?? null };
    }
  }

  /** ¿La especialidad de la Cx nueva viene del paso (no se cambia)? */
  public get newProcedureSpecialtyLocked(): boolean {
    return !!this.generalForm?.get('specialty')?.value;
  }

  public get newProcedureSpecialtyLabel(): string {
    const id = this.generalForm?.get('specialty')?.value;
    return this.specialtyList.find(s => s?.id === id)?.name ?? '';
  }

  /** "¿Quisiste decir?": Cx ya dadas de alta parecidas a lo que se escribe. */
  public get similarProcedures(): any[] {
    const typed = normName(this.newProcedure?.name);
    if (typed.length < 3) return [];
    const words = typed.split(' ').filter(w => w.length >= 3);
    const scored = this.procedureList
      .filter(p => p && p.active !== false)
      .map(p => {
        const n = normName(p.name);
        let score = similarity(typed, n);
        const tokens = n.split(' ');
        if (words.length && words.every(w => tokens.some(t => t.startsWith(w) || similarity(w, t) >= 0.8))) score = Math.max(score, 0.85);
        if (n.includes(typed) || typed.includes(n)) score = Math.max(score, 0.9);
        return { p, score };
      })
      .filter(x => x.score >= 0.72)
      .sort((a, b) => b.score - a.score || String(a.p.name).localeCompare(String(b.p.name), 'es'));
    return scored.slice(0, 6).map(x => x.p);
  }

  public specialtyName(id: any): string {
    return this.specialtyList.find(s => s?.id === id)?.name ?? 'Sin especialidad';
  }

  /** Usar una Cx que ya existe en vez de duplicarla. */
  useExistingProcedure(p: any): void {
    if (p?.specialty) this.generalForm.get('specialty')?.setValue(p.specialty);
    this.generalForm.get('procedure')?.setValue(p.id);
    this.creatingProcedure = false;
    this.newProcedure = { name: null, specialty: null };
  }

  async createProcedure(): Promise<void> {
    const specialty = this.generalForm.get('specialty')?.value ?? this.newProcedure.specialty ?? null;
    const name = String(this.newProcedure.name ?? '').trim();
    if (!name || !specialty) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'La Cx necesita nombre y especialidad.', dismissBtn: true });
      return;
    }
    // Ya existe con el mismo nombre en la especialidad: se usa esa (no se duplica).
    const same = this.procedureList.find(p => p && normName(p.name) === normName(name) && (!p.specialty || p.specialty === specialty));
    if (same) {
      await this.customMessageService.open({ title: 'Ya existe', type: 'warning', dismissBtn: true,
        message: `La Cx <b>${same.name}</b> ya está dada de alta; se usa esa.` });
      this.useExistingProcedure(same);
      return;
    }
    const similar = this.similarProcedures;
    if (similar.length) {
      const ok = await this.customMessageService.open({ title: '¿Quisiste decir?', type: 'warning',
        message: `Ya hay Cx parecidas: <b>${similar.slice(0, 3).map(p => p.name).join('</b>, <b>')}</b>. ¿Seguro que quieres dar de alta <b>${name}</b>?`,
        acceptBtn: true, acceptBtnText: 'Sí, es nueva', dismissBtn: true, dismissBtnText: 'Revisar' });
      if (!ok) return;
    }
    this.newProcedure.name = name;
    try {
      const response: any = await this.procedureService.create({ name, specialty, active: true });
      if (response.state === 'success') {
        const created = response.data[0];
        this.procedureList.push(created);
        this.generalForm.get('specialty')?.setValue(specialty);
        this.generalForm.get('procedure')?.setValue(created.id);
        this.creatingProcedure = false;
        this.newProcedure = { name: null, specialty: null };
      }
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  onHospitalChange(): void {
    const place = this.generalForm.get('hospital')?.value;
    // Hospital y médico están en el mismo paso: si se cambia el hospital y el
    // médico elegido no opera ahí, se limpia para volver a elegirlo.
    const doctor = this.doctorList.find(d => d?.id === this.generalForm.get('doctor')?.value);
    const doctorPlaces = this.doctorPlaces(doctor);
    if (place && doctor && doctorPlaces.length && !doctorPlaces.includes(place) && !this.record) {
      this.generalForm.get('doctor')?.setValue(null);
    }
    // Solo lockers tipo LOCKER (los Inhouse no se usan para sacar material);
    // se conserva el ya elegido en un borrador para no perderlo.
    this.warehouseOptions = this.warehousesList.filter(w => w.place === place
      && (w.storageType !== 'INHOUSE' || w.id === this.selectedWarehouse));
    if (this.warehouseOptions.length === 1) this.selectedWarehouse = this.warehouseOptions[0].id;
    this.computeFolioPreview();
    void this.refreshPreference();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Técnico Medtronic y folio por hospital
  // ─────────────────────────────────────────────────────────────────────────

  private static normalizeRole(name: any): string {
    return String(name ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  }

  /**
   * Usuarios con rol Técnico. Si el usuario ACTUAL es técnico, queda
   * preseleccionado y fijo (se muestra su nombre); para los demás roles se
   * despliega el selector radial.
   */
  private async loadTechnicians(): Promise<void> {
    const [rolesResponse, usersResponse, me] = await Promise.all([
      this.rolesService.search({ searchType: 'default', condition: { deleted: false } }),
      this.usersService.search({ searchType: 'default', condition: { deleted: false } }),
      this.usersService.loadCurrentUser(),
    ]);
    const techRoleIds = new Set(
      ((rolesResponse.data as any[]) ?? [])
        .filter(r => r && InterventionsWizardComponent.normalizeRole(r.name) === 'tecnico')
        .map(r => r.id),
    );
    this.technicianList = ((usersResponse.data as any[]) ?? [])
      .filter(u => u && u.role && techRoleIds.has(u.role) && u.active !== false)
      .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), 'es'));

    if (me && me.role && techRoleIds.has(me.role)) {
      this.technicianLocked = true;
      this.technicianLockedName = me.name ?? '';
      if (!this.technicianList.some(u => u.id === me.id)) this.technicianList.push(me);
      this.generalForm.get('technician')?.setValue(me.id);
      this.generalForm.get('technician')?.disable();
    }
  }

  public technicianName(id: string | null): string {
    if (!id) return '—';
    return this.technicianList.find(u => u.id === id)?.name ?? this.technicianLockedName ?? '—';
  }

  // ── Fecha y hora ──────────────────────────────────────────────────────

  /** Nuevo folio: fecha y hora actuales por omisión (se pueden cambiar). */
  private setDefaultDatetime(): void {
    const now = new Date();
    this.interventionDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.interventionTimeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    this.updateInterventionDatetime();
  }

  /** Compone interventionDatetime (YYYY-MM-DDTHH:mm) desde calendario + hora. */
  public updateInterventionDatetime(): void {
    const date = this.interventionDate;
    const time = this.interventionTimeStr;
    if (!date || !time) {
      this.generalForm.get('interventionDatetime')?.setValue(null);
      return;
    }
    const d = new Date(date);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${time}`;
    this.generalForm.get('interventionDatetime')?.setValue(value);
  }

  /** Al reanudar un borrador, regresa la fecha/hora guardadas a los campos.
   *  (startHour y finishHour van directo al form: son inputs type=time.) */
  private decomposeDatetimes(record: any): void {
    if (record.interventionDatetime) {
      const d = new Date(record.interventionDatetime);
      if (!isNaN(d.getTime())) {
        this.interventionDate = d;
        this.interventionTimeStr = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
    }
  }

  /** El folio es un campo abierto: solo refleja lo capturado. */
  public computeFolioPreview(): void {
    this.folioPreview = this.generalForm?.get('fileNumber')?.value || this.record?.fileNumber || null;
  }

  /** true si otro folio (no este registro) ya usa ese número. */
  private async folioTaken(fileNumber: string): Promise<boolean> {
    try {
      const r: any = await this.interventionsService.search({ searchType: 'default', condition: { deleted: false, fileNumber } });
      return ((r?.data as any[]) ?? []).some(x => x?.id !== this.record?.id
        && String(x?.fileNumber ?? '').trim().toUpperCase() === fileNumber.toUpperCase());
    } catch { return false; }
  }

  /**
   * Guarda los datos generales (crea el borrador con su folio o lo actualiza).
   * `draft = true` es "Guardar borrador y salir": basta con hospital y fecha
   * (lo que necesita el folio y la tabla); el resto se completa después.
   * Devuelve true si quedó guardado.
   */
  /** Fin de Cx anterior (o igual) al inicio: no se permite guardar. Formato HH:mm (24 h). */
  get hoursInvalid(): boolean {
    const norm = (v: any) => {
      const m = String(v ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
      return m ? Number(m[1]) * 60 + Number(m[2]) : null;
    };
    const start = norm(this.generalForm?.get('startHour')?.value);
    const end = norm(this.generalForm?.get('finishHour')?.value);
    return start !== null && end !== null && end <= start;
  }

  async saveStep1(advance: boolean, draft = false): Promise<boolean> {
    const folioCtrl = this.generalForm.get('fileNumber');
    folioCtrl?.setValue(String(folioCtrl?.value ?? '').trim() || null, { emitEvent: false });
    const missingForDraft = !folioCtrl?.value || !this.generalForm.get('hospital')?.value || !this.generalForm.get('interventionDatetime')?.value;
    if (draft ? missingForDraft : this.generalForm.invalid) {
      this.generalForm.markAllAsTouched();
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning',
        message: draft ? 'Para guardar el borrador captura al menos el folio, el hospital y la fecha/hora de la cirugía.' : 'Faltan campos obligatorios en los datos generales.',
        dismissBtn: true });
      return false;
    }
    if (this.hoursInvalid) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning',
        message: `La hora de <b>fin de Cx</b> (${this.generalForm.get('finishHour')?.value}) no puede ser anterior a la hora de <b>inicio</b> (${this.generalForm.get('startHour')?.value}).`,
        dismissBtn: true });
      return false;
    }
    const contract = this.contractList[0];
    if (!contract) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: 'No hay contrato activo para generar el folio.', dismissBtn: true });
      return false;
    }
    if (await this.folioTaken(folioCtrl!.value)) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning',
        message: `El folio <b>${folioCtrl!.value}</b> ya existe. Captura otro folio.`, dismissBtn: true });
      return false;
    }
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Guardando borrador...', loading: true });
    try {
      // getRawValue: el control de técnico puede estar deshabilitado (usuario técnico fijo).
      const data: any = { ...this.generalForm.getRawValue() };
      data['contract'] = contract.id;
      data['otherItems'] = this.otherItems;
      // Equipo de cirugía: se guarda como un solo objeto (charola, cabezal,
      // lentes, torre, SONICISION, energía de otra casa comercial).
      data['surgicalEquipment'] = { ...this.equipo };
      // Borradores viejos pudieron quedar con ambos prendidos: SONICISION gana.
      if (data['surgicalEquipment'].sonicisionUsed) {
        data['surgicalEquipment'].otherEnergyUsed = false;
        data['surgicalEquipment'].otherEnergyDetail = null;
      }
      if (!this.record) {
        // El folio (fileNumber) viene capturado en el formulario.
        // Borrador con status 'Incompleto': así aparece en la tabla de Salida
        // de material hasta que se completa el asistente (pasa a Pendiente).
        data['status'] = 'Incompleto';
        data['wizardStep'] = 1;
        data['operationType'] = 'single';
        const response: any = await this.interventionsService.create(data);
        if (response.state !== 'success') throw new Error(response.message);
        this.record = response.data[0];
        this.folioPreview = this.record.fileNumber;
      } else {
        data['id'] = this.record.id;
        data['wizardStep'] = Math.max(Number(this.record.wizardStep ?? 1), 1);
        data['operationType'] = 'single';
        const response: any = await this.interventionsService.update(data);
        if (response.state !== 'success') throw new Error(response.message);
        this.record = { ...this.record, ...response.data[0] };
        this.folioPreview = this.record.fileNumber;
      }
      this.onHospitalChange();
      await this.loadPackagesForContract();
      this.customMessageService.close();
      if (advance) this.step = 2;
      return true;
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Paso 2 — Material (la pieza se aparta AL AGREGARLA)
  // ─────────────────────────────────────────────────────────────────────────

  private decorate(stockItem: any): any {
    if (!stockItem) return null;
    const product = this.productsObject[stockItem.product];
    return { ...stockItem, code: product?.code ?? stockItem.product, description: product?.description ?? '—' };
  }

  public get filteredRowProducts(): any[] {
    const f = this.rowProductFilter.trim().toLowerCase();
    if (!f) return [];
    return this.productsList
      .filter(p => String(p.code ?? '').toLowerCase().includes(f) || String(p.description ?? '').toLowerCase().includes(f))
      .slice(0, 30);
  }

  /** Validación común y reserva inmediata de una pieza. */
  private async reserveAndAdd(stockItem: any): Promise<boolean> {
    if (this.pieces.some(p => p.id === stockItem.id)) return false;

    if (stockItem.status !== 'Disponible') {
      await this.customMessageService.open({
        title: 'Información', type: 'neutral',
        message: `La pieza tiene estatus <b>${stockItem.status}</b>: no se puede cargar. Si está en Transito, ya la tiene otro vale.`,
        dismissBtn: true,
      });
      return false;
    }
    if (stockItem.warehouse !== this.selectedWarehouse) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'La pieza pertenece a OTRO locker.', dismissBtn: true });
      return false;
    }
    const surgeryDate = this.generalForm.get('interventionDatetime')?.value;
    if (surgeryDate && stockItem.expiration && String(stockItem.expiration).slice(0, 10) < String(surgeryDate).slice(0, 10)) {
      const proceed = await this.customMessageService.open({
        title: 'Cuidado', type: 'warning',
        message: `La pieza caduca el <b>${stockItem.expiration}</b>, antes de la cirugía. ¿Cargarla de todos modos?`,
        dismissBtn: true, acceptBtn: true,
      });
      if (!proceed) return false;
    }

    const response: any = await this.stockService.update({ id: stockItem.id, tagRfid: stockItem.tagRfid, status: 'Transito' });
    if (response.state !== 'success') return false;
    this.pieces.push(this.decorate({ ...stockItem, status: 'Transito' }));
    await this.persistPieces();
    return true;
  }

  async scanTag(): Promise<void> {
    const tag = this.scanValue?.trim().toUpperCase();
    this.scanValue = '';
    if (!tag) return;
    if (!isValidRfidTag(tag)) {
      await this.customMessageService.open({ title: 'Etiqueta inválida', type: 'warning', message: `La etiqueta <b>${tag}</b> tiene ${tag.length} caracteres; debe tener exactamente ${RFID_TAG_LENGTH}.`, dismissBtn: true });
      return;
    }
    if (this.pieces.some(p => p.tagRfid === tag)) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: `La etiqueta <b>${tag}</b> ya está en la lista.`, dismissBtn: true });
      return;
    }
    try {
      const response = await this.stockService.search({ searchType: 'default', condition: { deleted: false, tagRfid: tag } });
      const stockItem = (response.data as any[])?.[0];
      if (!stockItem) {
        await this.customMessageService.open({ title: 'Error', type: 'error', message: `Etiqueta <b>${tag}</b> no encontrada en el inventario.`, dismissBtn: true });
        return;
      }
      await this.reserveAndAdd(stockItem);
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  /** Piezas sin etiqueta: se agregan por renglón (producto + lote + cantidad). */
  async addByRow(): Promise<void> {
    const { product, batch, quantity } = this.rowAdd;
    if (!product || !quantity || quantity < 1) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Elige insumo y cantidad.', dismissBtn: true });
      return;
    }
    try {
      const condition: any = { deleted: false, warehouse: this.selectedWarehouse, product, status: ['Disponible'] };
      if (batch) condition['batch'] = batch;
      const response = await this.stockService.search({ searchType: 'default', condition });
      const candidates = ((response.data as any[]) ?? [])
        .filter(x => x && !x.tagRfid && !this.pieces.some(p => p.id === x.id));
      if (candidates.length < quantity) {
        await this.customMessageService.open({
          title: 'Información', type: 'neutral',
          message: `Solo hay <b>${candidates.length}</b> piezas sin etiqueta disponibles de ese renglón (pediste ${quantity}).`,
          dismissBtn: true,
        });
        if (candidates.length === 0) return;
      }
      let added = 0;
      for (const item of candidates.slice(0, quantity)) {
        if (await this.reserveAndAdd(item)) added++;
      }
      this.rowAdd = { product: null, batch: null, quantity: 1 };
      this.rowProductFilter = '';
      if (added > 0) {
        await this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: `${added} piezas apartadas.`, duration: 1500 });
      }
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  async removePiece(piece: any): Promise<void> {
    const response = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Quitar la pieza <b>${piece.code}</b> (${piece.tagRfid || 'sin etiqueta'})? Regresará a Disponible.`,
      dismissBtn: true, acceptBtn: true,
    });
    if (!response) return;
    try {
      await this.stockService.update({ id: piece.id, tagRfid: piece.tagRfid, status: 'Disponible' });
      this.pieces = this.pieces.filter(p => p.id !== piece.id);
      await this.persistPieces();
      if (this.selectedPackageId) this.classify();
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  /** Guarda products.delivery (ids, formato compatible con el flujo actual). */
  private async persistPieces(): Promise<void> {
    if (!this.record) return;
    await this.interventionsService.update({
      id: this.record.id,
      operationType: 'single',
      products: { ...(this.record.products ?? {}), delivery: this.pieces.map(p => p.id) },
      supplier: { ...(this.record.supplier ?? {}), wizardWarehouse: this.selectedWarehouse },
      wizardStep: Math.max(Number(this.record.wizardStep ?? 1), 2),
    }).then((response: any) => {
      if (response.state === 'success') this.record = { ...this.record, ...response.data[0] };
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Lectura RFID por dispositivo (bandeja / anaquel / pistola vía Jetstream)
  // ─────────────────────────────────────────────────────────────────────────

  /** Al cambiar de modo se propone el dispositivo del almacén seleccionado. */
  public onRfidModeChange(): void {
    this.readTags = [];
    if (this.rfidMode === 'widget') return;
    const warehouse = this.warehousesList.find(w => w.id === this.selectedWarehouse);
    if (warehouse?.rfidDevice && !this.deviceName) this.deviceName = warehouse.rfidDevice;
  }

  /** Extrae EPCs de las distintas formas de respuesta de Jetstream. */
  private extractEpcs(payload: any): string[] {
    const out = new Set<string>();
    const walk = (node: any): void => {
      if (!node) return;
      if (typeof node === 'string') { if (node.trim()) out.add(node.trim().toUpperCase()); return; }
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (typeof node === 'object') {
        for (const key of ['EPCList', 'EpcList', 'epclist', 'EPCs', 'Epcs', 'Tags', 'TagList']) {
          if (node[key]) walk(node[key]);
        }
        for (const key of ['EPC', 'Epc', 'epc', 'TagId', 'Value']) {
          if (typeof node[key] === 'string') walk(node[key]);
        }
        if (node.Events) walk(node.Events);
      }
    };
    walk(payload);
    return [...out];
  }

  /**
   * Lee el dispositivo (bandeja / anaquel: epclist del lector; pistola:
   * último evento del handheld), cruza los EPCs contra el inventario del
   * almacén y muestra lo leído con checkboxes.
   */
  async readDevice(): Promise<void> {
    if (this.rfidMode !== 'pistola' && !this.deviceName.trim()) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Indica el nombre del dispositivo lector.', dismissBtn: true });
      return;
    }
    this.reading = true;
    this.readTags = [];
    try {
      const payload = this.rfidMode === 'pistola'
        ? await this.jetstreamService.scanning(this.deviceName.trim())
        : await this.jetstreamService.scan(this.deviceName.trim());
      const epcs = this.extractEpcs(payload);
      if (epcs.length === 0) {
        await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'El dispositivo no reportó etiquetas.', dismissBtn: true });
        return;
      }
      const response = await this.stockService.search({
        searchType: 'default',
        condition: { deleted: false, warehouse: this.selectedWarehouse, tagRfid: epcs },
      });
      const byTag = new Map(((response.data as any[]) ?? []).filter(x => x?.tagRfid)
        .map(x => [String(x.tagRfid).toUpperCase(), x]));
      this.readTags = epcs.map(epc => {
        const stockItem = byTag.get(epc) ?? null;
        const already = !!stockItem && this.pieces.some(piece => piece.id === stockItem.id);
        return {
          epc,
          stockItem,
          already,
          // preseleccionadas las que sí se pueden cargar
          selected: !!stockItem && !already && stockItem.status === 'Disponible',
        };
      });
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    } finally {
      this.reading = false;
    }
  }

  public get selectableTags(): number {
    return this.readTags.filter(t => t.stockItem && !t.already).length;
  }

  public toggleAllTags(value: boolean): void {
    for (const tag of this.readTags) {
      if (tag.stockItem && !tag.already) tag.selected = value;
    }
  }

  /** Aparta todas las piezas seleccionadas de la lectura. */
  async addSelectedTags(): Promise<void> {
    const chosen = this.readTags.filter(t => t.selected && t.stockItem && !t.already);
    if (chosen.length === 0) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'No hay etiquetas seleccionadas por cargar.', dismissBtn: true });
      return;
    }
    let added = 0;
    for (const tag of chosen) {
      if (await this.reserveAndAdd(tag.stockItem)) { added++; tag.already = true; tag.selected = false; }
    }
    if (added > 0) {
      await this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: `${added} piezas apartadas de la lectura.`, duration: 1500 });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Otros — insumos fuera de inventario (CFN / piezas / lote)
  // ─────────────────────────────────────────────────────────────────────────

  async addOther(): Promise<void> {
    if (!this.otherAdd.cfn || !this.otherAdd.pieces || this.otherAdd.pieces < 1) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Captura CFN y piezas.', dismissBtn: true });
      return;
    }
    this.otherItems.push({ cfn: String(this.otherAdd.cfn).trim().toUpperCase(), pieces: this.toInt(this.otherAdd.pieces, 1), batch: this.otherAdd.batch || null });
    this.otherAdd = { cfn: null, pieces: 1, batch: null };
    await this.persistOthers();
  }

  async removeOther(index: number): Promise<void> {
    this.otherItems.splice(index, 1);
    await this.persistOthers();
  }

  private async persistOthers(): Promise<void> {
    if (!this.record) return;
    const response: any = await this.interventionsService.update({
      id: this.record.id, operationType: 'single', otherItems: this.otherItems,
    });
    if (response.state === 'success') this.record = { ...this.record, ...response.data[0] };
  }

  async saveStep2(advance: boolean): Promise<void> {
    if (advance && this.pieces.length === 0 && this.otherItems.length === 0) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Carga al menos una pieza (o un renglón en Otros) antes de continuar.', dismissBtn: true });
      return;
    }
    await this.persistPieces();
    if (advance) {
      await this.loadPackagesForContract();
      this.buildSuggestions();
      this.step = 3;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Paso 3 — Paquete (sugerido por cobertura)
  // ─────────────────────────────────────────────────────────────────────────

  /** Id del "paquete" armado con los insumos del CIR (servicio hospitalario) elegido en el paso 2. */
  static readonly SERVICE_PKG = '__CIR__';

  /**
   * Paquete virtual con los insumos del servicio hospitalario (CIR) elegido
   * en los datos generales. Sus renglones traen `product` (id) y/o `code`;
   * `maxQuantity` vacío = comparte la cantidad del renglón anterior
   * (alternativas, p. ej. trocar de 12, 11 o 5 mm).
   */
  private servicePackage(): any | null {
    const service = this.hospitalServicesList.find(s => s?.id === (this.generalForm?.get('service')?.value ?? this.record?.service));
    if (!service) return null;
    const byCode: Record<string, any> = Object.fromEntries(this.productsList.map(p => [String(p.code ?? '').trim().toUpperCase(), p]));
    let lastQty = 1;
    const items = ((service.products ?? []) as any[]).map(row => {
      if (!row) return null;
      const isObj = typeof row === 'object';
      const productId = isObj ? (row.product ?? byCode[String(row.code ?? '').trim().toUpperCase()]?.id) : row;
      const q = isObj ? Number(row.maxQuantity) : NaN;
      if (Number.isFinite(q) && q > 0) lastQty = q;
      const prod = this.productsObject?.[productId];
      return productId ? {
        product: productId,
        code: prod?.code ?? (isObj ? row.code : '') ?? '',
        description: prod?.description ?? (isObj ? row.description : '') ?? '',
        quantity: lastQty,
      } : null;
    }).filter(Boolean);
    const cir = service.cir || this.generalForm?.get('cir')?.value || '';
    return {
      id: InterventionsWizardComponent.SERVICE_PKG,
      name: `CIR ${cir}${service.name ? ' — ' + service.name : ''}`.trim(),
      code: cir,
      fromService: true,
      configurations: [{ code: InterventionsWizardComponent.SERVICE_PKG, name: 'CIR del servicio', items }],
    };
  }

  private async loadPackagesForContract(): Promise<void> {
    let contractPkgs: any[] = [];
    if (this.record?.contract) {
      const response = await this.servicePackagesService.search({
        searchType: 'default', condition: { deleted: false, contract: this.record.contract },
      });
      contractPkgs = ((response.data as any[]) ?? []).filter(p => p && p.active !== false);
    }
    // Primero el CIR que se eligió en los datos generales (el material se compara contra él).
    const fromService = this.servicePackage();
    this.packagesList = fromService ? [fromService, ...contractPkgs] : contractPkgs;
    if (fromService && !this.selectedPackageId) this.selectedPackageId = fromService.id;
  }

  /** Insumos del CIR elegido (para marcar en el paso de material qué va dentro / fuera). */
  private cirCache: { key: string; ids: Set<string> } | null = null;
  get cirProductIds(): Set<string> | null {
    const serviceId = this.generalForm?.get('service')?.value ?? this.record?.service;
    if (!serviceId) return null;
    const key = serviceId + '|' + this.productsList.length;
    if (this.cirCache?.key !== key) {
      const pkg = this.servicePackage();
      this.cirCache = { key, ids: new Set(((pkg?.configurations?.[0]?.items ?? []) as any[]).map(i => String(i.product))) };
    }
    return this.cirCache.ids;
  }
  inCir(piece: any): boolean | null {
    const ids = this.cirProductIds;
    return ids ? ids.has(String(piece?.product)) : null;
  }

  /** Mayor cobertura entre los paquetes del contrato (para la etiqueta "Mejor cobertura"). */
  get bestContractCoverage(): number {
    return Math.max(0, ...this.suggestions.filter(s => !s.pkg.fromService).map(s => s.covered));
  }

  /** Cuenta cuántas piezas cargadas cubre cada paquete y ordena de mayor a menor. */
  buildSuggestions(): void {
    const loadedByProduct: { [id: string]: number } = {};
    for (const piece of this.pieces) loadedByProduct[piece.product] = (loadedByProduct[piece.product] ?? 0) + 1;

    this.suggestions = this.packagesList.map(pkg => {
      const conf = pkg.configurations?.[0] ?? { items: [] };
      let covered = 0;
      for (const item of conf.items ?? []) {
        covered += Math.min(loadedByProduct[item.product] ?? 0, Number(item.quantity) || 0);
      }
      return { pkg, covered, totalItems: (conf.items ?? []).length, pct: this.pieces.length ? Math.round(covered * 100 / this.pieces.length) : 0 };
    }).sort((a, b) => (b.pkg.fromService ? 1 : 0) - (a.pkg.fromService ? 1 : 0) || b.covered - a.covered);
    // El reparto (cubiertas / fuera de CIR) se muestra de inmediato para el CIR elegido.
    if (this.selectedPackageId) this.classify();
  }

  /** Reparte las piezas entre cubiertas y fuera de paquete (codicioso por renglón). */
  classify(): void {
    const pkg = this.packagesList.find(p => p.id === this.selectedPackageId);
    const items = pkg?.configurations?.[0]?.items ?? [];
    const remaining: { [id: string]: number } = {};
    for (const item of items) remaining[item.product] = (remaining[item.product] ?? 0) + (Number(item.quantity) || 0);

    this.coveredPieces = [];
    this.outPieces = [];
    for (const piece of this.pieces) {
      if ((remaining[piece.product] ?? 0) > 0) {
        remaining[piece.product]--;
        this.coveredPieces.push(piece);
      } else {
        this.outPieces.push(piece);
      }
    }
  }

  async selectPackage(pkgId: string | null): Promise<void> {
    this.selectedPackageId = pkgId;
    this.classify();
  }

  async saveStep3(advance: boolean): Promise<void> {
    if (!this.record) return;
    const pkg = this.packagesList.find(p => p.id === this.selectedPackageId) ?? null;
    // Material fuera del CIR: se debe documentar quién o por qué se agregó.
    const reason = String(this.outOfCirReason ?? '').trim();
    if (advance && this.outPieces.length > 0 && !reason) {
      await this.customMessageService.open({
        title: 'Falta información', type: 'warning', dismissBtn: true,
        message: `Hay <b>${this.outPieces.length}</b> pieza(s) fuera del CIR. Escribe quién las pidió o por qué se agregaron.`,
      });
      return;
    }
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Guardando CIR...', loading: true });
    try {
      // COPIA congelada de las partidas: si mañana cambia el paquete en el
      // contrato, esta cirugía no cambia sola.
      const response: any = await this.interventionsService.update({
        id: this.record.id,
        operationType: 'single',
        // El CIR del servicio no es un paquete del contrato: se guarda su copia en packageItems.
        servicePackage: pkg?.fromService ? null : (pkg?.id ?? null),
        packageConfiguration: pkg?.configurations?.[0]?.code ?? null,
        packageItems: pkg ? structuredClone(pkg.configurations?.[0]?.items ?? []) : null,
        outOfCirReason: this.outPieces.length > 0 ? (reason || null) : null,
        wizardStep: Math.max(Number(this.record.wizardStep ?? 1), 3),
      });
      if (response.state === 'success') this.record = { ...this.record, ...response.data[0] };
      this.customMessageService.close();
      if (advance) this.step = 4;
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Paso 5 — Vale
  // ─────────────────────────────────────────────────────────────────────────

  private name(list: any[], id: string): string {
    return list.find(x => x?.id === id)?.name ?? '—';
  }

  async generateVale(): Promise<void> {
    if (!this.record) return;
    // Si se llegó al PDF sin pasar por el paso del CIR, se toma el CIR del servicio elegido.
    if (!this.packagesList.length) await this.loadPackagesForContract();
    if (this.selectedPackageId) this.classify();
    // Formato FOLIO DE CIRUGÍA de Grupo Ángeles (GA_Folio.pdf): solo los
    // códigos que se capturaron en el folio, agrupados por código + lote.
    const r = this.record;
    const eq: any = { ...(r.surgicalEquipment ?? {}), ...this.equipo };
    const familyOf = (code: string) => {
      const product = this.productsList.find(p => p?.code === code);
      const fam = this.familiesList.find(f => f?.id === product?.family);
      return String(fam?.name ?? product?.productGroup ?? '').trim().toUpperCase();
    };
    const grouped = new Map<string, any>();
    for (const piece of this.pieces) {
      const key = `${piece.code}|${piece.batch ?? ''}`;
      const row = grouped.get(key) ?? { cot: familyOf(piece.code), code: piece.code ?? '', description: piece.description ?? '', batch: piece.batch ?? '', pieces: 0 };
      row.pieces += 1;
      grouped.set(key, row);
    }
    for (const other of this.otherItems) {
      grouped.set(`OTROS|${other.cfn}|${other.batch}`, { cot: 'Otros', code: other.cfn ?? '', description: 'Fuera de inventario', batch: other.batch ?? '', pieces: Number(other.pieces ?? 0) });
    }
    const dt = r.interventionDatetime ? new Date(r.interventionDatetime) : null;
    const date = dt && !isNaN(dt.getTime())
      ? `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}` : '';
    const place = this.placesList.find(p => p?.id === (r.hospital?.id ?? r.hospital));
    const tray = eq.tray === 'OTRA' ? (eq.trayOther ?? '') : (this.kitsList.find(k => k?.id === eq.tray)?.name ?? eq.tray ?? '');
    const observations = [
      this.outPieces.length && this.outOfCirReason?.trim() ? `Fuera de CIR: ${this.outOfCirReason.trim()}` : '',
      eq.otherEnergyUsed && eq.otherEnergyDetail ? `Energía de otra casa comercial: ${eq.otherEnergyDetail}` : '',
      r.comments ? String(r.comments) : (this.generalForm.get('comments')?.value ?? ''),
    ].filter(Boolean);

    const doc = buildFolioDoc({
      group: 'GRUPO ANGELES',
      date, folio: r.fileNumber ?? '',
      surgeon: titleCaseName(this.name(this.doctorList, r.doctor)).replace(/^—$/, ''),
      patient: this.patientName(r.patient).replace(/^—$/, ''),
      nurse: '',
      technician: this.technicianName(r.technician).replace(/^—$/, ''),
      surgery: this.name(this.procedureList, r.procedure).replace(/^—$/, ''),
      startHour: r.startHour ?? this.generalForm.get('startHour')?.value ?? '',
      finishHour: r.finishHour ?? this.generalForm.get('finishHour')?.value ?? '',
      hospital: place?.alias || place?.name || '',
      service: (() => { const sv = this.hospitalServicesList.find(x => x?.id === r.service); return sv ? (sv.mdtCode || sv.name || '') : ''; })(),
      cir: r.cir ?? '', episode: r.episode ?? '',
      tray, tower: eq.torre ?? '', head: eq.cabezal ?? '',
      lens: [eq.lente1, eq.lente2, eq.lente3].filter(Boolean).join(' / '),
      soni: eq.sonicisionUsed ? [eq.generator1, eq.generator2].filter(Boolean).join(' / ') : '',
      operationRoom: this.operationRoomList.find(x => x?.id === r.operationRoom)?.name ?? r.operationRoom ?? '',
      items: [...grouped.values()],
      observations,
    });
    this.gaPdf.download(doc, `Folio-${r.fileNumber ?? ''}`);
    this.valeGenerated = true;
  }

  async finishWizard(): Promise<void> {
    if (!this.record) return;
    if (!this.valeGenerated) {
      const proceed = await this.customMessageService.open({
        title: 'Cuidado', type: 'warning',
        message: 'Todavía no generas el vale impreso. ¿Completar de todos modos?',
        dismissBtn: true, acceptBtn: true,
      });
      if (!proceed) return;
    }
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Completando intervención...', loading: true });
    try {
      const response: any = await this.interventionsService.update({
        id: this.record.id, operationType: 'single',
        status: 'Pendiente', wizardStep: 5,
      });
      if (response.state !== 'success') throw new Error(response.message);
      this.record = { ...this.record, status: 'Pendiente', wizardStep: 5 };
      this.customMessageService.close();
      await this.customMessageService.open({
        title: '¡Hecho!', type: 'success',
        message: `Folio <b>${this.record.fileNumber}</b> completado. Carga el vale firmado para cerrarlo (o hazlo después desde la lista con "Continuar folio").`,
        duration: 3000,
      });
      this.step = 5; // Paso de Firma
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────

  async saveDraftAndExit(): Promise<void> {
    // Antes solo navegaba: si el borrador no existía no se creaba, y los
    // cambios de los datos generales hechos en este paso se perdían.
    // Ahora guarda los datos generales (crea el borrador si hace falta).
    const saved = await this.saveStep1(false, true);
    if (!saved) return;
    // El material ya quedó apartado al agregarlo; se asegura la lista.
    if (this.record && this.pieces.length > 0) await this.persistPieces();
    await this.customMessageService.open({
      title: 'Borrador guardado', type: 'success',
      message: this.record
        ? `El borrador <b>${this.record.fileNumber}</b> se puede retomar desde la lista de intervenciones.`
        : 'No se pudo crear el borrador.',
      duration: 2500,
    });
    this.router.navigate(['/interventions/folios']);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Paso 7 — Firma: vale firmado → piezas Consumido, folio Firmado
  // ─────────────────────────────────────────────────────────────────────────

  get isSigned(): boolean { return ['Realizada', 'Firmado', 'T. Facturacion', 'Facturado'].includes(this.record?.status); }

  /** Carga (o reemplaza) el vale firmado: foto o PDF. */
  openSignedFile(): void {
    if (!this.record) return;
    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value: this.signedKey,
        label: `Vale firmado · ${this.record.fileNumber ?? ''}`,
        accept: 'image/*,.pdf,application/pdf',
        uploadPath: 'interventions',
        uploadFolio: this.record.id,
      },
      width: '680px', maxHeight: '90vh',
    });
    ref.afterClosed().subscribe((key: string | null) => {
      if (key === null || key === undefined) return; // cancelado
      this.signedKey = key;
    });
  }

  /** Firma el folio: todas las piezas cargadas pasan a Consumido y el folio a Firmado. */
  async signFolio(): Promise<void> {
    if (!this.record) return;
    if (!this.signedKey) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Primero carga el vale firmado (foto o PDF).', dismissBtn: true });
      return;
    }
    const ok = await this.customMessageService.open({
      title: 'Confirmar firma', type: 'warning',
      message: `Las <b>${this.pieces.length}</b> piezas del folio <b>${this.record.fileNumber}</b> pasan a <b>Consumido</b> y el folio a <b>Firmado</b>. ¿Continuar?`,
      dismissBtn: true, acceptBtn: true, acceptBtnText: 'Firmar folio',
    });
    if (!ok) return;
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Registrando consumo...', loading: true });
    try {
      const response: any = await this.interventionsService.sign({
        intervention: this.record.id,
        file: { key: this.signedKey, fileName: String(this.signedKey).split('/').pop() },
      });
      this.customMessageService.close();
      if (response?.state !== 'success') throw new Error(response?.message || 'No se pudo firmar el folio.');
      const result = response.data?.[0] ?? {};
      this.record = { ...this.record, status: result.status ?? 'Firmado', attachments: result.attachments ?? this.record.attachments };
      await this.customMessageService.open({
        title: '¡Folio realizado!', type: 'success',
        message: `Folio <b>${this.record.fileNumber}</b> firmado: ${result.consumed ?? this.pieces.length} pieza(s) consumidas.`,
        duration: 2800,
      });
      this.router.navigate(['/interventions/folios']);
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err instanceof Error ? err.message : err), dismissBtn: true });
    }
  }

  async goToStep(index: number): Promise<void> {
    if (this.signOnly && index !== 5) return;   // fuera de turno: solo el paso de firma
    // Navegación LIBRE: se puede entrar a cualquier paso aunque el anterior
    // no esté terminado. Cada paso se defiende solo (el 2 pide guardar los
    // datos generales antes de apartar material; el 5 avisa si falta el vale).
    if (index === 3) { await this.loadPackagesForContract(); this.buildSuggestions(); if (this.selectedPackageId) this.classify(); }
    if (index === 4) { this.classify(); }
    this.step = index;
  }

  /**
   * Cerrar SIN guardar. Si el borrador ya existe, cerrarlo así lo descarta:
   * las piezas apartadas regresan a Disponible, la remisión en borrador se
   * cancela y la intervención se elimina (con su folio). Si todavía no hay
   * borrador (no se completó el paso 1), simplemente se sale.
   */
  async closeWithoutSaving(): Promise<void> {
    if (!this.record) {
      this.router.navigate(['/interventions/folios']);
      return;
    }
    const confirmed = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `Vas a cerrar <b>sin guardar</b>: el borrador <b>${this.record.fileNumber}</b> se descartará, `
        + `sus ${this.pieces.length} piezas apartadas regresarán a Disponible. `
        + `¿Descartar el borrador?`,
      dismissBtn: true, acceptBtn: true, acceptBtnText: 'Descartar', dismissBtnText: 'Volver',
    });
    if (!confirmed) return;

    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Descartando borrador...', loading: true });
    try {
      if (this.pieces.length > 0) {
        await this.stockService.update({
          operationType: 'multiple',
          records: this.pieces.map(p => ({ id: p.id, tagRfid: p.tagRfid, status: 'Disponible' })),
        });
      }
      await this.interventionsService.update({
        id: this.record.id, operationType: 'single', status: 'Cancelada', deleted: true,
      });
      this.customMessageService.close();
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
      return;
    }
    this.router.navigate(['/interventions/folios']);
  }
}
